// Placeholders that copy the markup of what they stand in for, so nothing moves when the data arrives.
// Each uses the real component's classes; only the text is replaced by shimmering lines.

import React from 'react';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import { MoreOutlined } from '@ant-design/icons';
import { t } from 'utils/i18n';

const range = (count: number): number[] => Array.from({ length: count }, (_, index) => index);

// a line of text: it sits in the line box of the text it replaces, so the height stays
export function SkeletonLine({ width }: { width: number | string }): JSX.Element {
    return <span className='cvat-skeleton-line cvat-preview-skeleton' style={{ width }} />;
}

// task or reference rows of the project page and the task/reference pages
export function RowSkeletons({ count }: { count: number }): JSX.Element {
    return (
        <div className='cvat-project-task-list' aria-busy>
            {range(count).map((index) => (
                <div key={index} className='cvat-project-task-row'>
                    <div className='cvat-project-task-preview cvat-preview-skeleton' />
                    <div className='cvat-project-task-body'>
                        <span className='cvat-project-task-title'><SkeletonLine width='30%' /></span>
                        <Text type='secondary'><SkeletonLine width='45%' /></Text>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function StepsSkeleton({ rows }: { rows: number }): JSX.Element {
    return (
        <>
            <div className='cvat-task-page-segments' aria-hidden>
                {range(rows).map((index) => <span key={index} className='cvat-task-page-segment' />)}
            </div>
            <ul className='cvat-task-page-steps' aria-busy>
                {range(rows).map((index) => (
                    <li key={index} className='cvat-task-page-step'>
                        <span className='cvat-task-page-step-icon' />
                        <span className='cvat-task-page-step-title'><SkeletonLine width='45%' /></span>
                        <Text type='secondary'><SkeletonLine width={90} /></Text>
                    </li>
                ))}
            </ul>
            <Text type='secondary' className='cvat-task-page-waiting'><SkeletonLine width='40%' /></Text>
        </>
    );
}

export function CategoriesSkeleton({ rows }: { rows: number }): JSX.Element {
    return (
        <>
            <div className='cvat-reference-page-bar' aria-hidden />
            <div className='cvat-reference-page-categories' aria-busy>
                {range(rows).map((index) => (
                    <div key={index} className='cvat-reference-page-category'>
                        <span className='cvat-reference-page-swatch cvat-preview-skeleton' />
                        <span className='cvat-task-page-step-title'><SkeletonLine width='50%' /></span>
                        <Text type='secondary'><SkeletonLine width={40} /></Text>
                    </div>
                ))}
            </div>
            <span className='cvat-reference-page-more-skeleton'><SkeletonLine width={110} /></span>
        </>
    );
}

export function TilesSkeleton({ count }: { count: number }): JSX.Element {
    return (
        <div className='cvat-frame-tiles' aria-busy>
            {range(count).map((index) => <div key={index} className='cvat-frame-tile cvat-preview-skeleton' />)}
        </div>
    );
}

interface PageProps {
    primary: string;
    strip: { title: string; action: string };
    middle: { title: string; body: React.ReactNode };
    last: string;
}

// the task and reference pages before their object arrived: breadcrumb, heading, three sections, aside
export function DetailPageSkeleton({
    primary, strip, middle, last,
}: PageProps): JSX.Element {
    return (
        <div className='cvat-task-page'>
            <div className='cvat-task-page-content' aria-busy>
                <nav aria-label={t('Breadcrumb')} className='cvat-task-page-crumbs'>
                    <span>{t('Projects')}</span>
                    <span>/</span>
                    <SkeletonLine width={90} />
                    <span>/</span>
                    <SkeletonLine width={70} />
                </nav>
                <div className='cvat-task-page-heading'>
                    <div className='cvat-task-page-heading-text'>
                        <h1 className='ant-typography'><SkeletonLine width={220} /></h1>
                        <Text type='secondary'><SkeletonLine width={260} /></Text>
                    </div>
                    <Button type='primary' disabled>{primary}</Button>
                    <Button aria-label={t('More actions')} icon={<MoreOutlined />} disabled />
                </div>
                <div className='cvat-task-page-columns'>
                    <div className='cvat-task-page-main'>
                        <section className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{strip.title}</h2>
                                <span className='cvat-task-page-spacer' />
                                <Button disabled>{strip.action}</Button>
                            </div>
                            <TilesSkeleton count={4} />
                        </section>
                        <section className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{middle.title}</h2>
                            </div>
                            {middle.body}
                        </section>
                        <section className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{last}</h2>
                            </div>
                            <RowSkeletons count={1} />
                        </section>
                    </div>
                    <aside className='cvat-task-page-aside'>
                        <div className='cvat-task-page-fact'>
                            <Text type='secondary'>{t('Assignee')}</Text>
                            <div className='cvat-skeleton-input cvat-preview-skeleton' />
                        </div>
                        <div className='cvat-task-page-fact'>
                            <Text type='secondary'>{t('Created')}</Text>
                            <span><SkeletonLine width={160} /></span>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
